import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { SimilarDatasetsComponent } from '../../../shared/components/similar-datasets/similar-datasets.component';
import { AssetCardData } from '../../../shared/components/asset-card/asset-card.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { AssetDiscussionListComponent } from '../../../shared/components/asset-discussion-list/asset-discussion-list.component';
import { DiscussionItem } from '../../discussions/discussion-home/discussion-home.component';
import { CatalogueApiService, UserInteractionApiService, SearchCriteria, DiscussionApiService } from '@angular-workspace/services';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';

@Component({
  selector: 'app-model-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    SimilarDatasetsComponent,
    ModalComponent,
    LoginModalComponent,
    AssetDiscussionListComponent
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class ModelDetailComponent implements OnInit, OnDestroy {
  private catalogueApiService = inject(CatalogueApiService);
  private helperService = inject(HelperService);
  private formatService = inject(FormatService);
  private keycloak = inject(KeycloakService);
  private userInteractionApiService = inject(UserInteractionApiService);
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakSubscription?: Subscription;

  modelId: string | null = null;
  modelDiscussions: DiscussionItem[] = [];
  modelDiscussionsLoading = false;
  modelDiscussionsError: string | null = null;
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Models', route: '/models' },
      { label: 'Model Details' }
    ],
    title: 'Model Details',
    description: 'Loading model information...'
  };
  similarModels: AssetCardData[] = [];
  modelDescription: string = '';
  modelTags: string[] = [];
  showTagsModal: boolean = false;
  readonly MAX_VISIBLE_TAGS = 8;

  modelDetails: {
    uploadedBy?: string;
    organisationType?: string;
    industry?: string;
    organizationName?: string;
    lastUpdated?: string;
    license?: string;
    modelType?: string;
    programmingLanguage?: string;
    downloadCount?: number;
    viewCount?: number;
    githubRepo?: string;
    mediaUrl?: string;
    [key: string]: any;
  } = {};

  modelCardDetails: {
    badge?: string;
    createdAt?: string;
    modelType?: string;
    programmingLanguage?: string;
    downloadCount?: number;
    viewCount?: number;
  } = {};

  // Store full model data to access publishStatus and other fields
  fullModelData: any = null;

  // Activity Overview metrics (from Item API)
  activityMetrics = {
    likes: 0,
    downloads: 0,
    views: 0,
    bookmarks: 0
  };

  isLoggedIn: boolean = false;
  isBookmarked: boolean = false;
  isLiked: boolean = false;
  showLoginModal: boolean = false;
  isLoading: boolean = true;

  activeTab: 'model' | 'details' | 'visualisation' | 'sql-playground' | 'discussion' = 'model';

  private route = inject(ActivatedRoute);

  setActiveTab(tab: 'model' | 'details' | 'visualisation' | 'sql-playground' | 'discussion'): void {
    this.activeTab = tab;
    if (tab === 'discussion') {
      this.loadModelDiscussions();
    }
  }

  get visibleTags(): string[] {
    return this.modelTags.slice(0, this.MAX_VISIBLE_TAGS);
  }

  get remainingTagsCount(): number {
    return Math.max(0, this.modelTags.length - this.MAX_VISIBLE_TAGS);
  }

  get allTags(): string[] {
    return this.modelTags;
  }

  get hasModelDetails(): boolean {
    return this.modelDetails && Object.keys(this.modelDetails).length > 0;
  }

  /** True when mediaUrl exists and is non-empty (show button) */
  get hasMediaUrl(): boolean {
    const url = this.modelDetails?.mediaUrl;
    return typeof url === 'string' && url.trim().length > 0;
  }

  /** True if mediaUrl contains github.com (show GitHub Repo); else show Redirect */
  get isMediaUrlGithub(): boolean {
    const url = this.modelDetails?.mediaUrl ?? '';
    return typeof url === 'string' && url.toLowerCase().includes('github.com');
  }

  async ngOnInit(): Promise<void> {
    await this.checkLoginStatus();
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          this.isLoggedIn = true;
        } else if (
          keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
          keycloakEvent.type === KeycloakEventType.OnAuthLogout
        ) {
          await this.checkLoginStatus();
          if (keycloakEvent.type === KeycloakEventType.OnAuthLogout) {
            this.isBookmarked = false;
            this.isLiked = false;
          }
        }
      }
    );
    this.route.paramMap.subscribe(params => {
      this.modelId = params.get('id');
      this.loadModelDetails();
    });
  }

  async loadModelDiscussions(): Promise<void> {
    if (!this.modelId) {
      this.modelDiscussions = [];
      return;
    }
    this.modelDiscussionsLoading = true;
    this.modelDiscussionsError = null;
    this.cdr.detectChanges();
    try {
      const response = await this.discussionApiService.getDiscussionChoices({
        filters: {
          type: [],
          status: ['APPROVED'],
          tags: [],
          sub_category_id: this.modelId
        },
        pinned: true,
        sort_by: 'newest',
        page: 1,
        limit: 10
      });
      const discussions = response?.data?.discussions ?? response?.discussions ?? [];
      this.modelDiscussions = (Array.isArray(discussions) ? discussions : []).map((item: any) =>
        this.mapDiscussionChoiceToItem(item)
      );
    } catch (error) {
      console.error('Error loading model discussions:', error);
      this.modelDiscussionsError = 'Failed to load discussions. Please try again.';
      this.modelDiscussions = [];
    } finally {
      this.modelDiscussionsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapDiscussionChoiceToItem(item: any): DiscussionItem {
    const author = item?.user?.name ?? 'Unknown';
    const subCategory = item?.sub_category ?? this.headerConfig.title ?? '';
    const publishedTime = item?.published_time ?? item?.created_at ?? '';
    return {
      id: item?.id ?? '',
      title: item?.title ?? '',
      description: `${author} · ${subCategory}`,
      author,
      category: item?.category ?? 'DATA_BANKS',
      replies: item?.votes ?? 0,
      views: item?.view_count ?? 0,
      lastActivity: publishedTime ? `Published ${this.formatRelativeTime(publishedTime)}` : '',
      isPinned: item?.is_pinned ?? false,
      isBookmarked: item?.is_bookmarked ?? false,
      isVoted: item?.is_voted ?? false,
      status: item?.status,
      subCategoryId: item?.sub_category_id,
      assetType: 'Model',
      assetName: subCategory
    };
  }

  private formatRelativeTime(dateString: string): string {
    const dateObj = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return dateObj.toLocaleDateString();
  }

  navigateToDiscussion(discussionId: string): void {
    this.discussionApiService.getReview(discussionId).then(() => {
      this.router.navigate(['/discussions', discussionId]);
    }).catch(() => {
      this.router.navigate(['/discussions', discussionId]);
    });
  }

  createNewDiscussion(): void {
    this.router.navigate(['/discussions/create-new'], {
      queryParams: { category: 'GETTING_STARTED', tab: 'getting-started' }
    });
  }

  ngOnDestroy(): void {
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async onBookmarkClick(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.modelId) return;
    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.modelId,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isBookmarked = !this.isBookmarked;
      if (this.isBookmarked) {
        this.toasterService.success("You've bookmarked the Model successfully.");
      } else {
        this.toasterService.success('Bookmark removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  async onLikeClick(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.modelId) return;
    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.modelId,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isLiked = !this.isLiked;
      // Optimistically update like count immediately (item API may have eventual consistency)
      this.activityMetrics.likes = Math.max(0, (this.activityMetrics.likes ?? 0) + (this.isLiked ? 1 : -1));
      if (this.isLiked) {
        this.toasterService.success("You've liked the Model successfully.");
      } else {
        this.toasterService.success('Like removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }

  /** Refresh activity metrics from item API after like */
  private async refreshActivityMetrics(): Promise<void> {
    if (!this.modelId) return;
    try {
      const response = await this.catalogueApiService.getItemById(this.modelId);
      let data = response;
      if (response?.result) data = Array.isArray(response.result) ? response.result[0] : response.result;
      else if (response?.data) data = Array.isArray(response.data) ? response.data[0] : response.data;
      else if (response?.items?.[0]) data = response.items[0];
      else if (Array.isArray(response) && response.length > 0) data = response[0];
      if (!data) return;
      const metrics = data.metrics || data;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = (metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0) + 1;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;
    } catch {
      // Ignore - keep existing metrics
    }
  }

  async checkBookmarkAndLikeStatus(): Promise<void> {
    if (!this.isLoggedIn || !this.modelId) return;
    try {
      const response = await this.userInteractionApiService.userInteractions({
        assetId: this.modelId,
        assetType: 'AI_MODEL',
      });
      const items: any[] =
        (response?.result && Array.isArray(response.result) ? response.result : null) ||
        (response?.results && Array.isArray(response.results) ? response.results : null) ||
        (response?.data && Array.isArray(response.data) ? response.data : null) ||
        [];
      const first = items[0] ?? {};
      const userInteraction = first?.userInteraction;
      const ad = first?.assetDetails ?? first;

      const bookmarked =
        typeof userInteraction?.bookmarked === 'boolean' ? userInteraction.bookmarked :
        typeof first?.isBookmarked === 'boolean' ? first.isBookmarked :
        typeof first?.bookmarked === 'boolean' ? first.bookmarked :
        typeof ad?.bookmarked === 'boolean' ? ad.bookmarked : null;
      if (bookmarked !== null) {
        this.isBookmarked = bookmarked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isBookmarked = actionTypes.includes('BOOKMARK');
      }

      const liked =
        typeof userInteraction?.liked === 'boolean' ? userInteraction.liked :
        typeof first?.isLiked === 'boolean' ? first.isLiked :
        typeof first?.liked === 'boolean' ? first.liked :
        typeof ad?.liked === 'boolean' ? ad.liked : null;
      if (liked !== null) {
        this.isLiked = liked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isLiked = actionTypes.includes('LIKE');
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking bookmark/like status:', error);
    }
  }

  async loadModelDetails(): Promise<void> {
    if (!this.modelId) {
      this.isLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = true;
    try {
      console.log('Loading model details for ID:', this.modelId);
      const response = await this.catalogueApiService.getItemById(this.modelId);
      console.log('API Response received:', response);

      let modelData = response;
      
      // Handle different response structures
      if (response?.result) {
        if (Array.isArray(response.result)) {
          modelData = response.result.length > 0 ? response.result[0] : null;
        } else {
          modelData = response.result;
        }
      } else if (Array.isArray(response) && response.length > 0) {
        modelData = response[0];
      } else if (response && typeof response === 'object' && !response.result) {
        // Response might be the model data directly
        modelData = response;
      }

      if (!modelData) {
        console.error('No model data found in response. Response structure:', JSON.stringify(response, null, 2));
        this.headerConfig.description = 'Failed to load model information. No data found in response.';
        this.isLoading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        return;
      }

      console.log('Model data loaded:', modelData);

      // Extract short description
      let shortDescription = modelData.shortDescription || modelData.summary || '';
      if (!shortDescription && modelData.description) {
        shortDescription = modelData.description.length > 150
          ? modelData.description.substring(0, 150).trim() + '...'
          : modelData.description;
      }

      // Extract organization name
      const organizationName = modelData.provider || modelData.organisation || modelData.organization || modelData.organisationName || '';

      // Update header config
      const modelTitle = modelData.label || modelData.name || 'Model Details';
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Models', route: '/models' },
          { label: modelTitle }
        ],
        title: modelTitle,
        description: shortDescription || 'Loading model information...',
        assetId: this.modelId || modelData.id || ''
      };

      if (this.isLoggedIn) {
        await this.checkBookmarkAndLikeStatus();
      }

      // Extract description
      this.modelDescription = modelData.description || '';

      // Extract tags
      this.modelTags = [];
      if (modelData.tags && Array.isArray(modelData.tags)) {
        this.modelTags = modelData.tags;
      }

      // Extract model details
      const organisationType = modelData.organizationType || modelData.organisationType || 'Public';
      const industry = modelData.industry || '';

      this.modelDetails = {
        uploadedBy: modelData.provider || modelData.organisation || modelData.organization || '',
        organisationType: organisationType,
        industry: industry,
        organizationName: organizationName,
        lastUpdated: this.formatService.formatDate(modelData.itemCreatedAt || modelData.createdAt || modelData.lastUpdated) || '',
        license: modelData.license || modelData.licence || '',
        modelType: modelData.modelType || modelData.type || '',
        programmingLanguage: modelData.language || modelData.programmingLanguage || modelData.codeLanguage || '',
        downloadCount: modelData.downloadCount || modelData.downloads || 0,
        viewCount: modelData.viewCount || modelData.views || 0,
        githubRepo: modelData.githubRepo || modelData.repository || '',
        mediaUrl: modelData.mediaURL || modelData.mediaUrl || '',
        publishStatus: modelData.publishStatus || modelData.publish_status || ''
      };

      // Reset to model tab if mediaUrl is present and user was on details/visualisation/sql-playground (tabs hidden)
      if (this.hasMediaUrl && (this.activeTab === 'details' || this.activeTab === 'visualisation' || this.activeTab === 'sql-playground')) {
        this.activeTab = 'model';
      }

      // Store full model data for accessing publishStatus and other fields
      this.fullModelData = modelData;

      // Populate Activity Overview from Item API metrics
      const metrics = modelData.metrics || modelData;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = (metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0) + 1;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;
      
      // Log publishStatus for debugging
      console.log('Model publishStatus:', {
        publishStatus: modelData.publishStatus || modelData.publish_status,
        fullModelDataPublishStatus: this.fullModelData?.publishStatus,
        modelDetailsPublishStatus: this.modelDetails?.['publishStatus']
      });

      const badge = this.helperService.extractAccessBadge(modelData);
      this.modelCardDetails = {
        badge: badge || 'Public',
        createdAt: this.formatService.formatDateInIST(modelData.itemCreatedAt || modelData.createdAt) || '',
        modelType: modelData.modelType || modelData.type || '',
        programmingLanguage: modelData.language || modelData.programmingLanguage || modelData.codeLanguage || '',
        downloadCount: modelData.downloadCount || modelData.downloads || 0,
        viewCount: modelData.viewCount || modelData.views || 0
      };

      this.isLoading = false;
      
      // Trigger change detection to ensure view updates
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Load similar models based on tags (after modelTags is set)
      this.loadSimilarModels().catch(err => console.error('Error loading similar models:', err));
      
      // Also trigger change detection in next microtask for robustness
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });

    } catch (error) {
      console.error('Error loading model details:', error);
      this.headerConfig.description = 'Failed to load model information.';
      this.isLoading = false;
      
      // Trigger change detection on error
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  async loadSimilarModels(): Promise<void> {
    if (!this.modelTags || this.modelTags.length === 0) {
      this.similarModels = [];
      return;
    }
    try {
      const searchCriteria: SearchCriteria[] = [
        { field: 'tags', searchType: 'term', values: this.modelTags },
        { field: 'type', searchType: 'term', values: ['adex:AiModel'] }
      ];
      const response = await this.catalogueApiService.searchAllAssets(
        '', [], [], 1, 10, 'name:asc;itemCreatedAt:asc;', searchCriteria
      );
      let items: any[] = [];
      if (Array.isArray(response)) items = response;
      else if (response?.result && Array.isArray(response.result)) items = response.result;
      else if (response?.items && Array.isArray(response.items)) items = response.items;
      else if (response?.data && Array.isArray(response.data)) items = response.data;
      else if (response?.data?.result && Array.isArray(response.data.result)) items = response.data.result;
      else if (response?.data?.items && Array.isArray(response.data.items)) items = response.data.items;
      this.similarModels = items
        .filter((item: any) => (item.id || item['@id'] || '') !== this.modelId)
        .map((item: any) => this.mapApiItemToAssetCard(item));
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading similar models:', error);
      this.similarModels = [];
    }
  }

  private mapApiItemToAssetCard(item: any): AssetCardData {
    const id = item.id || item['@id'] || '';
    const title = item.label || item.name || 'Untitled';
    const provider = item.provider || item.organisation || item.organization || item.provider_name || '';
    const tags: string[] = Array.isArray(item.tags) ? [...item.tags] : [];
    const metadata: any = {};
    if (item.resourceServer) {
      const rs = Array.isArray(item.resourceServer) ? item.resourceServer[0] : item.resourceServer;
      metadata.type = rs?.type || rs?.name || '';
    }
    metadata.format = item.fileFormat || item.format || '';
    metadata.date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || item.created_at || new Date());
    const badgeLabel = this.helperService.extractAccessBadge(item) || 'Public';
    const badgeColor = this.helperService.getBadgeColorVariant(badgeLabel);
    return { id, title, provider, tags, metadata, badge: { label: badgeLabel, color: badgeColor as any } };
  }

  openTagsModal(): void {
    this.showTagsModal = true;
  }

  onGithubRepoClick(): void {
    // Use mediaURL from response (prioritize mediaURL over mediaUrl)
    const mediaURL = this.modelDetails.mediaUrl;
    if (mediaURL) {
      window.open(mediaURL, '_blank');
    } else {
      console.warn('No mediaURL found for model');
    }
  }

  onRequestAccess(): void {
    // TODO: Implement request access functionality
    console.log('Request access clicked');
  }

  get isPublishStatusPending(): boolean {
    // Check if publishStatus is PENDING
    // Check multiple possible locations for publishStatus
    const publishStatus = 
      this.fullModelData?.publishStatus || 
      this.fullModelData?.publish_status || 
      this.modelDetails?.['publishStatus'] ||
      '';
    
    const isPending = publishStatus && typeof publishStatus === 'string' && publishStatus.toUpperCase().trim() === 'PENDING';
    
    return isPending;
  }

  get isPublishStatusDeclined(): boolean {
    const publishStatus = 
      this.fullModelData?.publishStatus || 
      this.fullModelData?.publish_status || 
      this.modelDetails?.['publishStatus'] ||
      '';
    const s = publishStatus && typeof publishStatus === 'string' ? publishStatus.toUpperCase().trim() : '';
    return s === 'DECLINED' || s === 'REJECTED' || s === 'DENIED';
  }

  /** Date part from formatted string (e.g. "02-Feb-2026") - match Dataset details */
  getCreatedAtDate(createdAt: string): string {
    if (!createdAt) return '';
    const parts = createdAt.split(' ');
    return parts[0] || '';
  }

  /** Time part from formatted string (e.g. "11:43 IST") - match Dataset details */
  getCreatedAtTime(createdAt: string): string | null {
    if (!createdAt) return null;
    const parts = createdAt.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : null;
  }
}
